/**
 * Each model's citation mix as a 100% stacked bar (2026-09-08).
 *
 * Answers Lisa's follow-up to the source donut (2026-09-01): "if we open it, do
 * we then want one for each one as well?" — one row per model, so you can see
 * that models draw on genuinely different evidence, which is invisible in the
 * table of counts this sits under.
 *
 * FORM: 100% stacked bars are the `dataviz` default for part-to-whole, and go
 * horizontal for long category names — which these are ("Peer-reviewed /
 * Society Publisher"). This is the one place in the report a stack is correct:
 * source categories are mutually exclusive and exhaustive, unlike the three
 * signals (which is why the old "signal mix" stack was removed in July).
 *
 * Colours come from the shared palette in the RUN-WIDE category order, so a
 * colour means the same source type here as in the donut above and in every
 * other model's row.
 */
import type { ModelSourceMix } from '@aurora/shared';
import { slot } from './palette';

const BAR_H = 20;
const ROW_GAP = 26;
const LABEL_W = 202;
const TRACK_W = 320;
const CHART_W = LABEL_W + TRACK_W + 56;
/** Surface-coloured spacer between segments (the 2px gap rule). */
const SEG_GAP = 1.5;

const pct = (v: number): string => {
  const p = v * 100;
  if (p > 0 && p < 1) return '<1%';
  return `${Math.round(p)}%`;
};

export function SourceMixByModel({
  mixes,
}: {
  mixes: ModelSourceMix[];
}): JSX.Element | null {
  if (mixes.length === 0) return null;

  const legend = mixes[0]?.slices ?? [];
  const basis = mixes[0]?.basis ?? 'appearances';
  const rowH = BAR_H + ROW_GAP;
  const chartH = mixes.length * rowH;

  return (
    <figure className="source-mix-model">
      <svg
        width="100%"
        viewBox={`0 0 ${CHART_W} ${chartH}`}
        preserveAspectRatio="xMinYMin meet"
        role="img"
        aria-label="Share of each model's citations by source type"
      >
        {mixes.map((m, mi) => {
          let x = LABEL_W;
          const y = mi * rowH;
          return (
            <g key={m.model}>
              <text
                x={LABEL_W - 8}
                y={y + BAR_H / 2}
                textAnchor="end"
                dominantBaseline="middle"
                className="chart-bar-label"
              >
                {m.model.length > 26 ? `${m.model.slice(0, 25)}…` : m.model}
              </text>
              <rect
                x={LABEL_W}
                y={y}
                width={TRACK_W}
                height={BAR_H}
                rx={3}
                className="chart-bar-track"
              />
              {m.slices.map((s, si) => {
                const w = s.share * TRACK_W;
                if (w <= 0) return null;
                const seg = (
                  <rect
                    key={s.label}
                    x={x}
                    y={y}
                    width={Math.max(1, w - SEG_GAP)}
                    height={BAR_H}
                    fill={slot(si)}
                  >
                    <title>
                      {m.model} — {s.label}: {pct(s.share)} ({s.citations} of{' '}
                      {m.totalCitations}{' '}
                      {basis === 'citations' ? 'links' : 'source uses'})
                    </title>
                  </rect>
                );
                x += w;
                return seg;
              })}
              {/* Direct-label only the dominant segment; never every segment. */}
              {m.slices[0] && m.slices[0].share > 0 && (
                <text
                  x={LABEL_W + 6}
                  y={y + BAR_H / 2}
                  dominantBaseline="middle"
                  className="chart-seg-label"
                >
                  {pct(m.slices[0].share)}
                </text>
              )}
              <text
                x={LABEL_W + TRACK_W + 6}
                y={y + BAR_H / 2}
                dominantBaseline="middle"
                className="chart-bar-value"
              >
                {m.totalCitations}
              </text>
            </g>
          );
        })}
      </svg>
      <div className="chart-legend">
        {legend.map((s, i) => (
          <span key={s.label} className="chart-legend-item">
            <span
              className="chart-legend-swatch"
              style={{ background: slot(i) }}
            />
            {s.label}
          </span>
        ))}
      </div>
      <figcaption className="muted ranked-bars-caption">
        Each bar is one model&apos;s own source use, split by type; the number on
        the right is that model&apos;s total. The percentage inside each bar is
        its largest source type, and colours match the chart above.{' '}
        {basis === 'appearances' ? (
          <>
            Counted as <strong>source activity</strong> — one count each time an
            answer drew on a source type — which is the same unit as the table
            above, and a different unit from the total link counts in the
            run-wide chart.
          </>
        ) : (
          <>Counted as cited links, the same unit as the run-wide chart.</>
        )}
      </figcaption>
    </figure>
  );
}
